import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import services from '../../services';
import { useParams } from 'react-router';
import ErrorIcon from '@material-ui/icons/Error';
import Table from '../../components/super/Table';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { useStores } from '../../hooks/use-stores';
import CircularProgress from '@material-ui/core/CircularProgress';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import { observer } from 'mobx-react';
import GetAppIcon from '@material-ui/icons/GetApp';

/**
 * Recursively collect all string values that start with s3:// from context (pdfUrl, pdfUrls, etc.).
 */
function getS3UrlsFromContext(obj, out = []) {
    if (obj == null) return out;
    if (typeof obj === 'string') {
        if (obj.startsWith('s3://')) out.push(obj);
        return out;
    }
    if (Array.isArray(obj)) {
        obj.forEach((item) => getS3UrlsFromContext(item, out));
        return out;
    }
    if (typeof obj === 'object') {
        Object.values(obj).forEach((value) => getS3UrlsFromContext(value, out));
        return out;
    }
    return out;
}

function getFileNameFromS3Url(s3Url) {
    try {
        const path = s3Url.replace(/^s3:\/\//, '').split('/');
        return path[path.length - 1] || 'download.pdf';
    } catch {
        return 'download.pdf';
    }
}

function NotificationTargets() {
    const { reasonId } = useParams();
    const [modalError, setModalError] = useState(null);
    const [showingContext, setShowingContext] = useState(false);
    const [sendEmailTo, setSendEmailTo] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [sendEmailMessage, setSendEmailMessage] = useState(null);
    const [downloadingUrl, setDownloadingUrl] = useState(null);
    const { superStore, commonStore, notificationsStore } = useStores();
    const { loadingReason, reason } = notificationsStore;
    const [statusFilter, setStatusFilter] = useState('all');
    const [counts, setCounts] = useState({
        all: 0,
        failed: 0,
        success: 0,
        planned: 0,
    });
    const [tableRefresh, setTableRefresh] = useState(0);
    const [resendingAll, setResendingAll] = useState(false);

    const contextFiles = reason?.contextJson
        ? getS3UrlsFromContext(reason.contextJson)
        : [];
    const uniqueContextFiles = [...new Set(contextFiles)];

    const handleDownloadFile = useCallback(
        async (s3Url) => {
            if (!reasonId || !s3Url) return;
            setDownloadingUrl(s3Url);
            try {
                const response = await services.Notifications.downloadFile(
                    reasonId,
                    s3Url
                );
                const blob = response?.data;
                const disposition =
                    response?.headers?.['content-disposition'];
                let fileName = getFileNameFromS3Url(s3Url);
                if (disposition) {
                    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (match && match[1]) {
                        fileName = match[1].replace(/['"]/g, '').trim();
                    }
                }
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName || 'download.pdf';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            } catch (e) {
                commonStore.error(
                    e?.response?.data?.message ||
                        e?.message ||
                        'Download failed'
                );
            } finally {
                setDownloadingUrl(null);
            }
        },
        [reasonId, commonStore]
    );
    const reloadTargets = useCallback(() => {
        setTableRefresh((n) => n + 1);
    }, []);

    const reSendNotification = useCallback(
        (record) => {
            commonStore.showConfirm(
                `Resend notification to  ${record.email}? `,
                'Resend',
                '',
                async () => {
                    let result = await superStore.resendNotification(
                        record.reason_id,
                        record.id
                    );
                    if (result) Object.assign(record, result);
                    reloadTargets();
                }
            );
        },
        [commonStore, superStore, reloadTargets]
    );

    const resendAllFailed = useCallback(() => {
        const failedCount = counts.failed || 0;
        commonStore.showConfirm(
            `Resend all ${failedCount} failed notification${
                failedCount === 1 ? '' : 's'
            }? They will be queued with a stagger to avoid SMTP rate limits.`,
            'Resend all failed',
            '',
            async () => {
                setResendingAll(true);
                try {
                    const result = await services.Notifications.targetsService(
                        reasonId
                    ).resendFailed();
                    commonStore.success(
                        `Queued ${result.queued || 0} failed notification${
                            (result.queued || 0) === 1 ? '' : 's'
                        } for resend`
                    );
                    reloadTargets();
                } catch (e) {
                    commonStore.error(
                        e?.response?.data?.message ||
                            e?.message ||
                            'Failed to resend'
                    );
                } finally {
                    setResendingAll(false);
                }
            }
        );
    }, [commonStore, counts.failed, reasonId, reloadTargets]);
    const columns = React.useMemo(
        () => [
            {
                Header: 'Created',
                accessor: 'created',
                Cell: ({ row: { original: x } }) => (
                    <div className="timespan">
                        {x.failed ? (
                            <ErrorIcon
                                className={'error-mark'}
                                onClick={() => {
                                    setModalError(x);
                                }}
                            />
                        ) : null}
                        {moment(x.created).format('llll')}
                    </div>
                ),
            },
            {
                Header: 'Sent',
                accessor: 'sent',
                Cell: ({ value }) =>
                    value ? moment(value).format('llll') : '',
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ row: { original: x } }) => {
                    if (x.failed) return 'Failed';
                    switch (x.status) {
                        case 0:
                            return 'Cancelled';
                        case 1:
                            return 'Planned';
                        case 2:
                            return 'Sent';
                        default:
                            return x.status;
                    }
                },
            },
            {
                Header: 'Email',
                accessor: 'email',
            },
            {
                Header: 'Attempts',
                accessor: 'attempts',
            },
            {
                Header: 'Mailgun id',
                accessor: 'mailgun_id',
            },
            {
                Header: 'Delivery Code',
                accessor: 'deliveryCode',
            },
            {
                Header: 'Delivered',
                id: 'delivered',
                accessor: 'deliveryDate',
                Cell: ({ value }) =>
                    value ? moment(value).format('llll') : '',
            },
            {
                Header: 'Opened',
                id: 'opened',
                accessor: 'openDate',
                Cell: ({ value }) =>
                    value ? moment(value).format('llll') : '',
            },
            {
                Header: '',
                id: 'actions',
                disableSortBy: true,
                accessor: (x) => {
                    return x.failed ? (
                        <Button
                            color={'primary'}
                            onClick={() => {
                                reSendNotification(x);
                            }}
                        >
                            Resend
                        </Button>
                    ) : null;
                },
            },
        ],
        [reSendNotification]
    );

    useEffect(() => {
        if (reasonId) notificationsStore.loadReason(reasonId);
    }, [reasonId]);

    const showContext = () => {
        setShowingContext(true);
    };
    const hideContext = () => {
        setShowingContext(false);
    };

    const updateStatus = useCallback(
        (newStatus) => {
            commonStore.showConfirm(
                'Switch status to ' + newStatus,
                'Change',
                'Switch status',
                async () => {
                    notificationsStore.update(reasonId, { status: newStatus });
                }
            );
        },
        [reasonId]
    );

    const handleSendSameEmail = useCallback(async () => {
        const email = (sendEmailTo || '').trim();
        if (!email || !email.includes('@')) {
            setSendEmailMessage({ type: 'error', text: 'Enter a valid email address.' });
            return;
        }
        setSendEmailMessage(null);
        setSendingEmail(true);
        try {
            const result = await services.Notifications.sendEmail(reasonId, email);
            setSendEmailMessage({ type: 'success', text: `Requested email to send: ${result.to} Result: ${result.message}` });
            setSendEmailTo('');
        } catch (e) {
            const text =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                'Failed to send email.';
            setSendEmailMessage({ type: 'error', text });
        } finally {
            setSendingEmail(false);
        }
    }, [reasonId, sendEmailTo]);

    // We'll start our table without any data
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [pageCount, setPageCount] = React.useState(0);
    const fetchIdRef = React.useRef(0);

    const fetchData = React.useCallback(
        async ({ pageSize, pageIndex, sortBy }) => {
            if (!reasonId) return;
            const fetchId = ++fetchIdRef.current;
            setLoading(true);

            const sort = sortBy && sortBy[0];
            const order = sort
                ? `${sort.id} ${sort.desc ? 'desc' : 'asc'}`
                : undefined;

            let result = await services.Notifications.targetsService(
                reasonId
            ).list({
                p: pageIndex,
                n: pageSize,
                s: null,
                f: null,
                status: statusFilter === 'all' ? undefined : statusFilter,
                order,
                fetchId,
            });

            if (fetchId === fetchIdRef.current) {
                setData(result.rows);
                setPageCount(result.pages);
                if (result.counts) setCounts(result.counts);
                setLoading(false);
            }
        },
        [reasonId, statusFilter, tableRefresh]
    );

    return (
        <>
            <Grid
                container
                direction="row"
                justify="center"
                align="center"
                spacing={2}
            >
                <Grid item xs={12}>
                    <Box mt={4}>
                        <Typography variant="h2">
                            Notification detail
                        </Typography>
                    </Box>
                </Grid>

                {loadingReason ? (
                    <CircularProgress />
                ) : (
                    reason && (
                        <Grid container item xs={12}>
                            <Grid item xs={4}>
                                Status:
                                <ButtonGroup
                                    color="primary"
                                    variant={'contained'}
                                >
                                    <Button
                                        disabled={reason.status === 0}
                                        color={
                                            reason.status === 0
                                                ? 'secondary'
                                                : 'primary'
                                        }
                                        onClick={() => {
                                            updateStatus(0);
                                        }}
                                    >
                                        Disabled
                                    </Button>
                                    <Button
                                        disabled={reason.status === 1}
                                        color={
                                            reason.status === 1
                                                ? 'secondary'
                                                : 'primary'
                                        }
                                        onClick={() => {
                                            updateStatus(1);
                                        }}
                                    >
                                        Planned
                                    </Button>
                                    <Button
                                        disabled={reason.status === 2}
                                        color={
                                            reason.status === 2
                                                ? 'secondary'
                                                : 'primary'
                                        }
                                        onClick={() => {
                                            updateStatus(2);
                                        }}
                                    >
                                        Processed
                                    </Button>
                                </ButtonGroup>
                            </Grid>

                            <Grid item xs={4}>
                                {showingContext ? (
                                    <>
                                        <Button
                                            color="primary"
                                            variant={'contained'}
                                            onClick={hideContext}
                                        >
                                            Hide Context
                                        </Button>
                                        <pre style={{ textAlign: 'left' }}>
                                            {JSON.stringify(
                                                reason.contextJson,
                                                null,
                                                2
                                            )}
                                        </pre>
                                    </>
                                ) : (
                                    <Button
                                        color="primary"
                                        variant={'contained'}
                                        onClick={showContext}
                                    >
                                        Show Context
                                    </Button>
                                )}
                            </Grid>
                            <Grid item xs={4}>
                                Reason: {reason.reason}
                            </Grid>
                        </Grid>
                    )
                )}
                {reason && uniqueContextFiles.length > 0 && (
                    <Grid container item xs={12} spacing={1} style={{ marginTop: 16 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                Files
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Files attached to this notification (from context). Click to download.
                            </Typography>
                        </Grid>
                        {uniqueContextFiles.map((s3Url, index) => (
                            <Grid item xs={12} sm={6} md={4} key={s3Url}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                        downloadingUrl === s3Url ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <GetAppIcon fontSize="small" />
                                        )
                                    }
                                    onClick={() => handleDownloadFile(s3Url)}
                                    disabled={downloadingUrl != null}
                                >
                                    {getFileNameFromS3Url(s3Url) || `File ${index + 1}`}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                )}
                {reason && (
                    <Grid container item xs={12} spacing={2} style={{ marginTop: 16 }}>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                Send the same email (same content and context as this notification) to another address. No new notification record is created; the email is sent directly.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Email"
                                type="email"
                                placeholder="recipient@example.com"
                                value={sendEmailTo}
                                onChange={(e) => setSendEmailTo(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSendSameEmail();
                                }}
                                disabled={sendingEmail}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} style={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSendSameEmail}
                                disabled={sendingEmail || !(sendEmailTo || '').trim()}
                            >
                                {sendingEmail ? (
                                    <CircularProgress size={22} color="inherit" />
                                ) : (
                                    'Send'
                                )}
                            </Button>
                        </Grid>
                        {sendEmailMessage && (
                            <Grid item xs={12}>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: sendEmailMessage.type === 'error' ? '#c62828' : '#2e7d32',
                                    }}
                                >
                                    {sendEmailMessage.text}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        justify="space-between"
                    >
                        <Grid item>
                            <ButtonGroup color="primary" variant="contained">
                                {['all', 'failed', 'success', 'planned'].map(
                                    (value) => {
                                        const labels = {
                                            all: 'All',
                                            failed: 'Failed',
                                            success: 'Success',
                                            planned: 'Planned',
                                        };
                                        const selected = statusFilter === value;
                                        const count = counts[value];
                                        return (
                                            <Button
                                                key={value}
                                                disabled={selected}
                                                color={
                                                    selected
                                                        ? 'secondary'
                                                        : 'primary'
                                                }
                                                onClick={() =>
                                                    setStatusFilter(value)
                                                }
                                            >
                                                {labels[value]}
                                                {count != null
                                                    ? ` (${count})`
                                                    : ''}
                                            </Button>
                                        );
                                    }
                                )}
                            </ButtonGroup>
                        </Grid>
                        <Grid item>
                            <Button
                                color="secondary"
                                variant="contained"
                                disabled={
                                    resendingAll || !counts.failed
                                }
                                onClick={resendAllFailed}
                            >
                                {resendingAll ? (
                                    <CircularProgress
                                        size={22}
                                        color="inherit"
                                    />
                                ) : (
                                    `Resend all failed${
                                        counts.failed
                                            ? ` (${counts.failed})`
                                            : ''
                                    }`
                                )}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Table
                        key={`${statusFilter}-${tableRefresh}`}
                        columns={columns}
                        data={data}
                        fetchData={fetchData}
                        loading={loading}
                        pageCount={pageCount}
                        enableSorting
                    />
                </Grid>
            </Grid>
            {modalError && (
                <Dialog
                    open={modalError != null}
                    onClose={() => setModalError(null)}
                >
                    <DialogTitle>
                        Error happens when sent to {modalError.email}
                    </DialogTitle>
                    <DialogContent>
                        <pre>{modalError.last_error}</pre>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setModalError(null)}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

export default observer(NotificationTargets);
